import Button, {
	ButtonColor,
} from 'components/button';
import HeaderAndValue from 'components/headers/HeaderAndValue';
import {
	Assignment,
	Status,
} from 'models';
import {
	useState,
} from 'react';
import {
	useTripService,
} from 'services';
import ModalDeviation from '../card/modals/deviations/ModalDeviation';
import {
	AssignmentFilterType,
	isDone,
} from 'utils/transport';
import ParkingLotSelector from '../parkingLotSelector/ParkingLotSelector';
import {
	TransportDeviation,
} from './TransportDeviation';
import {
	SvgElectrical,
	SvgHighPriority,
	SvgDoneNoCircle,
} from 'components/svg/Svg';
import {
	toastWarn,
} from 'utils/toast/toast';
import './transportListCardDetails.scss';
import {
	DangerousGoodsField,
} from './dangerousGoods/DangerousGoodsField';
import {
	ModalWithFooter,
} from 'components/modal';
import LoadingWheel from 'components/loadingWheel/LoadingWheel';
import Checkbox from 'components/checkbox/Checkbox';
import Typography, {
	TypographyType,
} from 'components/typography/karim/Typography';
import {
	updateOpenGateCounter,
	handleOpenGateCounter,
} from './gate/GateUtil';

interface TripCardStatsDataProps {
	data: Assignment,
	expanded: boolean,
}

const twoDigits = (val: number) => ('0'+val).slice(-2);

const getTimeSlot = (data: Assignment, isPickup: boolean) => {
	const location = isPickup ? 'source' : 'destination';
	const startTime = new Date(data?.route?.[location]?.time?.start);
	const endTime = new Date(data?.route?.[location]?.time?.end);

	return `${twoDigits(startTime.getHours())}:${twoDigits(startTime.getMinutes())} - ${
		twoDigits(endTime.getHours())}:${twoDigits(endTime.getMinutes())}`;
}

const TripCardStatsData = ({
	data,
	expanded,
}: TripCardStatsDataProps) => {
	const [show, setShow] = useState(false);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [showExtraConfirmationForPower, setShowExtraConfirmationForPower] = useState(false);
	const {
		patchParkingLot,
		patchStatus,
		openGate,
		isLoading: isOpenGateInProgress,
	} = useTripService();

	const handleShow = () => setShow(true);
	const isAssignmentDone = isDone(data);
	const isPickup = data?.direction === AssignmentFilterType[AssignmentFilterType.PICKUP];

	const location = isPickup ? 'source' : 'destination';
	const container = data?.goods?.uniqueIdentification;
	const timeslot = getTimeSlot(data, isPickup);
	const date = data?.date ? new Date(data.date).toLocaleDateString('en-GB').split('/').join('.') : '';
	const address = data?.route?.[location]?.address?.street + ', ' + data?.route?.[location]?.address?.zip + ' ' + data?.route?.[location]?.address?.city;
	const gln = data?.route?.[location]?.gln;
	const weight = data?.goods?.weight;
	const temperatureControlled = data?.goods?.temperatureControlled;
	const parkingLot = data?.route?.[location]?.address?.parkingLot;
	const priority = data?.goods?.priority === 'A';
	const references = data?.references;
	const transporter = data?.transporter;
	const isReadyForPickup = isPickup && parkingLot && data.status === 'ReadyForPickup';

	const [checkedOne, setCheckedOne] = useState(false);
	const [confirmedChecks, setConfirmedChecks] = useState(false);
	const [shouldShowConfirmPopUp, setShouldShowConfirmPopUp] = useState(false);
	const [hasClickedOpenGate, setHasClickedOpenGate] = useState(false);

	const openGateCounter = () => {
		return updateOpenGateCounter(container, gln, date);
	}

	const handleGateAccess = async () => {
		const {
			shouldOpenGate,
			shouldShowConfirm,
		} = await handleOpenGateCounter(data, container, gln, date);

		let supportiveText;
		if (parkingLot) {
			supportiveText = isPickup ? `Trallen skal hentes på parkeringsplass: ${parkingLot}`
				: `Trallen skal settes på parkeringsplass: ${parkingLot}`;
		}

		else {
			supportiveText = 'Vi har foreløpig ikke mottatt noen parkeringplass til din tralle.';
		}

		if (shouldOpenGate) {
			openGate(data?.id, gln, supportiveText, data?.status === Status.Planned, openGateCounter);
		}
		setShouldShowConfirmPopUp(shouldShowConfirm);
	};

	const handleParkingLotChange = (lot: string) => {
		// To test pickup locally, you can force setting parkinglot
		// Then, remember also to set disabled={isPickup} to false
		// patchParkingLot(data?.id, lot, 'source');
		patchParkingLot(data?.id, lot);
	};

	const handleStatusChange = () => {
		if (!parkingLot) {
			toastWarn('Ukjent parkeringsplass',
				'Det er ikke mulig å avslutte transporten før en parkeringsplass er satt.', 15000);
		}
		else {
			setShowConfirmation(true);
		}
	}

	const handleChangeOne = () => {
		setCheckedOne(!checkedOne);
		setConfirmedChecks(!checkedOne);
	};

	// TODO: Bedre håndtering av det å legge til checkboxer; denne ble lagt til raskt for å få med endringene p.t.
	const ExtraConfirmationCheckboxes = () => {
		if (temperatureControlled) {
			let text;
			if (priority && isPickup) {
				text = 'Har du husket å starte aggregatet?';
			}
			else if (isPickup) {
				text = 'Har du husket å koble fra strøm?';
			}
			else {
				text = 'Har du husket å koble til strøm?';
			}
			return (
				<div className="transport-list-card-confirmation">
					<Typography type={TypographyType.textBaseSemiBold}>
					Bekreft også følgende:
					</Typography>
					<Checkbox
						label={text}
						checked={checkedOne}
						onChange={handleChangeOne}
					/>
				</div>
			);
		}
	}

	return (
		<>
			<ModalWithFooter
				buttonTextConfirm="Åpne porten"
				buttonTextClose={`Bekreft ${isPickup ? 'henting' : 'levering'}`}
				show={shouldShowConfirmPopUp}
				title={`Du har ikke bekreftet ${isPickup ? 'henting' : 'levering'}`}
				isSubmitDisabled={false}
				onConfirmation={() => {
					openGate(data?.id, gln, '', data?.status === Status.Planned, openGateCounter);
					setShouldShowConfirmPopUp(false);
				}}
				onClose={() => {
					setShowConfirmation(true);
					setHasClickedOpenGate(true);
					setShouldShowConfirmPopUp(false);
				}}
			>
				Vil du bekrefte først?
			</ModalWithFooter>
			<ModalWithFooter
				show={showConfirmation}
				title={`Bekreft ${isPickup ? 'henting' : 'levering'}`}
				isSubmitDisabled={!confirmedChecks && temperatureControlled}
				onConfirmation={() => {
					patchStatus(data?.id, isPickup ? 'Loaded' : 'Unloaded');
					if (hasClickedOpenGate) {
						setHasClickedOpenGate(false);
						openGate(data?.id, gln, '', data?.status === Status.Planned, openGateCounter);
					}
				}}
				onClose={() => {
					setShowExtraConfirmationForPower(false);
					setShowConfirmation(false);
				}}
			>
				Du er i ferd med å bekrefte at tralle {container} er {isPickup ? 'hentet' : 'levert'}.
				{ExtraConfirmationCheckboxes()}

			</ModalWithFooter>

			<div className="transport-list-card-details">
				<div style={{
					display: 'flex',
				}}>
					<div style={{
						flex: '0 0 40%',
					}}>
						<HeaderAndValue header="Tralle" value={container} />
					</div>
					<div style={{
						flex: '0 0 30%',
					}}>
						{!expanded && (
							<HeaderAndValue
								header="Parkeringsplass"
								value={(
									<span>
										{parkingLot || 'Ukjent'}
										{isReadyForPickup && (<SvgDoneNoCircle />)}
									</span>
								)}
							/>
						)}
					</div>
					<div style={{
						flex: '0 0 30%',
					}}>
						<HeaderAndValue header={isPickup ? 'Hentevindu' : 'Leveringsvindu'} value={timeslot} align="right" />
					</div>
				</div>

				<div style={{
					display: 'flex',
				}}>
					<div style={{
						flex: '1 1 auto',
					}}>
						<HeaderAndValue
							header="Parkeringsplass"
							value={
								<div
									style={{
										marginTop: '5px',
									}}
								>
									<Button
										disabled={isPickup}
										color={ButtonColor.SECONDARY}
										className="transport-list-card-details-parking-button"
										onClick={handleShow}
									>
										{parkingLot || '?'}
									</Button>

									{isReadyForPickup && (
										<span style={{
											fontWeight: 600,
											fontSize: '12px',
											color: '#32872c',
										}}>
											<SvgDoneNoCircle
												style={{
													marginTop: '-5px',
												}}
											/>
											Klar til henting
										</span>
									)}

									<ParkingLotSelector
										currentLot={parkingLot}
										location={gln}
										onChange={lot => handleParkingLotChange(lot)}
										show={show}
										setShow={setShow}
									/>
								</div>
							}
						/>
					</div>
					<div style={{
						flex: '1 1 auto',
						textAlign: 'right',
					}}>
						<HeaderAndValue header="Dato" value={date} align="right" />
					</div>
				</div>

				<div style={{
					display: 'flex',
				}}>
					<div style={{
						flex: '1 1 auto',
					}}>
						<HeaderAndValue header="Adresse" value={address} />
					</div>
					<div style={{
						flex: '1 1 auto',
						textAlign: 'right',
					}}>
						<HeaderAndValue
							header="Vekt"
							value={`${new Intl.NumberFormat('no-NB').format(weight)} kg`}
							align="right"
						/>
					</div>
				</div>

				<div style={{
					display: 'flex',
				}}>
					<div style={{
						flex: '1 1 auto',
					}}>
						<HeaderAndValue header="Referanse" value={`${references?.aao || ''} ${references?.co ? '(' + references.co + ')' : ''}`} />
					</div>
					{transporter?.vehicle?.licencePlate && (
						<div style={{
							flex: '1 1 auto',
							textAlign: 'right',
						}}>
							<HeaderAndValue
								header="Regnr trekkvogn"
								value={transporter?.vehicle?.licencePlate}
								align="right"
							/>
						</div>
					)}
				</div>

				<TransportDeviation assignment={data} />

				<DangerousGoodsField assignment={data} />

				{(temperatureControlled || isPickup) && (
					<div>
						<HeaderAndValue
							header="Detaljer"
							value={
								<>
									<div style={{
										marginLeft: '-5px',
									}}>
										{temperatureControlled && (
											<div style={{
												margin: '0 5px',
											}}>
												<SvgElectrical done={isAssignmentDone}/>
												{' '}
												Temperaturkontrollert
											</div>
										)}
										{isPickup && (
											<div style={{
												margin: '0 5px',
											}}>
												{priority ? (
													<>
														<SvgHighPriority done={isAssignmentDone} />
														{' Trallen hentes på båten'}
													</>
												) : (
													<>
														Trallen hentes på kai
													</>
												)}
											</div>
										)}
									</div>
								</>
							}
						/>
					</div>
				)}

				<div
					style={{
						display: 'flex',
						flexDirection: isDone(data) ? 'row' : 'column',
					}}
				>
					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<div
							style={{
								justifyContent: 'center',
							}}
						>
							<Button
								color={ButtonColor.SECONDARY}
								disabled={isOpenGateInProgress}
								onClick={() => handleGateAccess()}
							>
								{isOpenGateInProgress && (
									<LoadingWheel width="1rem" height="1rem" />
								)}

								{!isOpenGateInProgress && (
									<span>Åpne porten</span>
								)}
							</Button>
						</div>
					</div>

					<div
						style={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<div
							style={{
								justifyContent: 'center',
							}}
						>
							<ModalDeviation
								assignment={data}
							>
								Registrere avvik
							</ModalDeviation>
						</div>
						{ !isDone(data) ?
							<div
								style={{
									justifyContent: 'center',
								}}
							>
								<Button
									onClick={() => handleStatusChange()}
									disabled={data?.status !== Status.Planned && data?.status !== Status.ReadyForPickup}
								>
									Bekreft {isPickup ? 'henting' : 'levering'}
								</Button>
							</div> : null
						}
					</div>
				</div>
				{ isDone(data) ?
					<div
						style={{
							fontWeight: '600',
							whiteSpace: 'nowrap',
							width: '100%',
							textAlign: 'center',
						}}
					>
						{isPickup ? 'Hentingen' : 'Leveringen'} er bekreftet
					</div> : null
				}
			</div>
		</>
	);
}

export default TripCardStatsData;